import React from "react";
import { FaLock } from "react-icons/fa";
import { Reward } from "../../types/reward.entity";
import { getRewardProviderMeta } from "../../utils/provider-meta";
import { isTremendousRewardProductMetadata } from "../../utils/tremendous";

interface RewardProviderSummaryProps {
  reward: Reward;
}

const SummaryItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">{label}</p>
    <p className="mt-2 text-sm font-semibold text-gray-800 break-words">{value}</p>
  </div>
);

export const RewardProviderSummary: React.FC<RewardProviderSummaryProps> = ({ reward }) => {
  const provider = getRewardProviderMeta(reward.provider.name);
  const metadata = reward.provider.productMetadata;
  const tremendousMetadata = isTremendousRewardProductMetadata(metadata) ? metadata : null;
  const amazonCurrency = typeof metadata?.currency === "string" ? metadata.currency : null;
  const amazonAmount = typeof metadata?.amount === "number" ? metadata.amount : null;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Step 1</p>
            <h3 className="mt-2 text-xl font-bold text-gray-800 flex items-center gap-2">
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${provider.gradient} text-white shadow-sm`}>
                {provider.icon}
              </span>
              Provider Mapping
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              This reward keeps its provider configuration read-only after creation.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
            <FaLock className="h-3 w-3" />
            Locked
          </span>
        </div>

        <SummaryItem label="Provider" value={provider.label} />

        {tremendousMetadata ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <SummaryItem label="Product" value={tremendousMetadata.productNameSnapshot} />
            <SummaryItem label="Product ID" value={tremendousMetadata.productId} />
            <SummaryItem
              label="Payout Value"
              value={`${tremendousMetadata.currencyCode} ${tremendousMetadata.denomination}`}
            />
            <SummaryItem
              label="Supported Range"
              value={`${tremendousMetadata.currencyCode} ${tremendousMetadata.skuMin} - ${tremendousMetadata.skuMax}`}
            />
            <SummaryItem
              label="Coverage"
              value={`${tremendousMetadata.countries.length} supported countries`}
            />
          </div>
        ) : amazonCurrency && amazonAmount ? (
          <SummaryItem label="Gift Card Value" value={`${amazonCurrency} ${amazonAmount}`} />
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">
            No extra provider-level metadata is stored for this reward.
          </div>
        )}
      </div>
    </div>
  );
};
